import { Text } from "@mantine/core";
import { DataTableColumn } from "mantine-datatable";
import { useCallback, useMemo } from "react";

import { WaiveApprovalStatus } from "../../../graphql/generated";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { Waive } from "../../../types/waive";
import { toLocalDate } from "../../../utils/date.ts";
import { toCurrencyPrice } from "../../../utils/number.ts";
import { toHumanReadable } from "../../../utils/string";
import { getUserDisplayName, isBababosTenant } from "../../../utils/user.ts";
import ActionIcon from "../../ActionIcon/ActionIcon";
import { DatabaseMetadataPopover } from "../../DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import SvgEdit from "../../Icons/Edit";
import Spoiler from "../../Spoiler/Spoiler.tsx";
import DisableButton from "../../Table/DisableButton.tsx";
import { TableData, TableRow } from "../../Table/types.ts";
import TableFilesDisplay from "../../TableFilesDisplay/TableFilesDisplay.tsx";
import { useStyles } from "../TableWaive.styles.ts";

interface Props {
  data: Waive[] | null | undefined;
  showActionsColumn?: boolean;
  onDownloadFile?: ClickHandler;
  onEdit?: (id: number) => void;
}

type ClickHandler = (id: Waive["id"], fieldName?: keyof Waive) => void;

export function useTableWaiveData({
  data,
  showActionsColumn,
  onEdit,
}: Props): TableData {
  const { classes } = useStyles();

  const { user } = useCurrentUserContext();

  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "createdAt",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Waive",
            fieldName: "createdAt",
          }}
        >
          Created Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 160,
    },
    {
      accessor: "customerName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Waive",
            fieldName: "customer",
          }}
        >
          Customer Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "requestNumber",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Waive",
            fieldName: "requestNumber",
          }}
        >
          Waive Request Number
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      hidden: !isBababosTenant(user),
      width: 230,
    },
    {
      accessor: "invoiceNumber",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "invoiceNumber",
          }}
        >
          Inv No
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 250,
    },
    {
      accessor: "lateCharge",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "lateCharge",
          }}
        >
          Late Charge
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 150,
    },
    {
      accessor: "amount",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Waive",
            fieldName: "amount",
          }}
        >
          Waive Amount
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 150,
    },
    {
      accessor: "requestDocument",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Waive",
            fieldName: "requestDocument",
          }}
        >
          Request Document
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "approvalStatus",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Waive",
            fieldName: "approvalStatus",
          }}
        >
          Approval Status
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 150,
    },
    {
      accessor: "approvalDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Waive",
            fieldName: "approvalDate",
          }}
        >
          Approval Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 150,
    },
    {
      accessor: "collectorAssigned",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Waive",
            fieldName: "collectorAssigned",
          }}
        >
          Collector Assigned
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 200,
    },
    {
      accessor: "approvalDocument",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Waive",
            fieldName: "approvalDocument",
          }}
        >
          Approval Document
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "notes",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Waive",
            fieldName: "notes",
          }}
        >
          Notes
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 200,
    },
    {
      accessor: "actions",
      title: "Actions",
      hidden: !showActionsColumn,
    },
  ];

  const handleEdit = useCallback(
    (id: number) => {
      onEdit && onEdit(id);
    },
    [onEdit]
  );

  const rows = useMemo(() => {
    return data?.map((item) => {
      const row: TableRow = {
        id: item.id,
        createdAt: toLocalDate(item.createdAt),
        customerName: item.invoice.customer.name,
        lateCharge: toCurrencyPrice(item.invoice.lateCharge),
        approvalDate: toLocalDate(item.approvalDate),
        collectorAssigned: item.collectorAssigned && (
          <Text className={classes.collectorAssignedWrapper}>
            {getUserDisplayName(item.collectorAssigned)}
          </Text>
        ),
        invoiceNumber: item.invoice.invoiceNumber,
        approvalStatus: toHumanReadable(item.approvalStatus),
        requestNumber: item.requestNumber,
        notes: item.notes && <Spoiler text={item.notes} />,
        amount: toCurrencyPrice(item.amount),
        requestDocument: item.requestDocument && (
          <TableFilesDisplay files={[item.requestDocument]} />
        ),
        approvalDocument: item.approvalDocument && (
          <TableFilesDisplay files={[item.approvalDocument]} />
        ),
        actions:
          item.approvalStatus !== WaiveApprovalStatus.Pending ? (
            <DisableButton
              text={`Editing is not allowed, waive status is ${toHumanReadable(
                item.approvalStatus
              )}`}
            />
          ) : (
            <ActionIcon onClick={() => handleEdit(item.id)}>
              <SvgEdit />
            </ActionIcon>
          ),
      };
      return row;
    });
  }, [data, classes, handleEdit]);

  return [columns, rows];
}
